import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgProgress } from '@ngx-progressbar/core';
import { ToastrService } from 'ngx-toastr';
import { zip } from 'rxjs';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ConfirmationComponent } from '../../../../shared/components/confirmation/confirmation.component';
import { ConfirmationModel } from '../../../../shared/model/confirmation.model';
import { UIDialogService } from '../../../../shared/services/ui-dialog.service';
import { SortUtils } from '../../../../shared/utils/sort.utils';
import { AuthService } from '../../../security/service/auth.service';
import { PartModel } from '../../model/part.model';
import { PartsUIMessages } from '../../model/parts-ui.message';
import { PartsService } from '../../service/parts.service';

@Component({
  selector: 'app-parts-detail',
  templateUrl:
    'console/app-js/modules/parts/components/parts-detail/parts-detail.component.html'
})
export class PartsDetailComponent extends APIOperableComponent
  implements OnInit {
  public loading = false;
  private partId: string;
  public form: FormGroup;
  public partDetail: PartModel;
  private partTypes = [];
  public partTypesFiltered = [];
  private partClassifications = [];
  public partClassificationsFiltered = [];
  private makes = [];
  public makesFiltered = [];
  private productLines = [];
  public productLinesFiltered = [];
  private partCategories = [];
  public partCategoriesFiltered = [];
  public statuses = [
    { id: true, name: 'Active' },
    { id: false, name: 'Inactive' }
  ];
  public trueFalseData = [{ id: true, name: 'Y' }, { id: false, name: 'N' }];
  private equipmentTypes = [];
  public equipmentTypesFiltered = [];
  public canManageParts = false;
  public canViewParts = false;
  public canViewCaps = false;
  public pageTitle: string;
  public uomData = [{ id: 'Each', name: 'Each' }];

  constructor(
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    public partsService: PartsService,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private dialogService: UIDialogService
  ) {
    super(progressService, toastr, authService);
    this.route.params.subscribe(params => {
      this.partId = params['id'];
      if (this.partId === 'add') {
        this.pageTitle = 'Add Part';
      } else {
        this.pageTitle = 'Part Details';
      }
    });
  }

  ngOnInit() {
    // VL-2482: Admin Portal part create/edit is disabled.
    this.canManageParts = false;
    this.canViewParts =
      this.authService.isViewerOrManager() || this.authService.isAdmin();
    this.canViewCaps = this.authService.isAdmin();
    if (this.partId === 'add') {
      this.router.navigate(['/console/parts']);
      return;
    }
    this.getData();
  }

  private getData() {
    this.progressRef.start();
    this.loading = true;
    zip(
      this.partsService.getPartDetails(this.partId),
      this.partsService.getPartTypes(),
      this.partsService.getMakes(),
      this.partsService.getPartClassifications(),
      this.partsService.getProductLines(),
      this.partsService.getPartCategories(),
      this.partsService.getEquipmentTypes()
    ).subscribe(
      data => {
        this.partDetail = data[0].results;
        this.partTypes = data[1];
        this.partTypesFiltered = data[1];
        this.makes = data[2];
        this.makesFiltered = data[2];
        this.partClassifications = data[3];
        this.partClassificationsFiltered = data[3];
        this.productLines = data[4];
        this.productLinesFiltered = data[4];
        this.partCategories = data[5];
        this.partCategoriesFiltered = data[5];
        this.equipmentTypes = data[6];
        this.equipmentTypesFiltered = data[6];
      },
      () => {
        this.onAPIError(PartsUIMessages.GET_PART_DETAILS_ERROR);
      },
      () => {
        this.createForm();
        if (this.partId !== 'add') {
          this.showInactive('make', this.makes);
          this.showInactive('partType', this.partTypes);
          this.showInactive('partClassification', this.partClassifications);
          this.showInactive('productLine', this.productLines);
          this.showInactive('partCategory', this.partCategories);
          this.showInactive('equipmentType', this.equipmentTypes);
        }
        this.progressRef.complete();
        this.loading = false;
      }
    );
  }

  public onSave() {
    if (!this.canManageParts) {
      return;
    }
    if (this.form.valid) {
      this.dialogService
        .open(ConfirmationComponent, [
          {
            provide: ConfirmationModel,
            useValue: new ConfirmationModel(
              PartsUIMessages.UPDATE_PART_CONFIRMATION_TITLE,
              PartsUIMessages.UPDATE_PART_CONFIRMATION_MESSAGE
            )
          }
        ])
        .then(confirmed => {
          if (confirmed) {
            this.progressRef.start();
            if (this.form.get('id').value) {
              this.partsService.updatePart(this.form.getRawValue()).subscribe(
                res => {
                  if (res && res.responseCode === 200) {
                    this.toastr.success(PartsUIMessages.UPDATE_PART_SUCCESS);
                  } else if (
                    res &&
                    res.responseCode === 400 &&
                    res.errorCode === 160
                  ) {
                    this.toastr.error(PartsUIMessages.ADD_PART_DUPLICATE);
                  } else {
                    this.toastr.error(PartsUIMessages.UPDATE_PART_ERROR);
                  }
                },
                error => {
                  this.toastr.error(error.errorMessage);
                },
                () => {
                  this.form.markAsPristine();
                  this.progressRef.complete();
                  this.partsService
                    .getPartDetails(this.partId)
                    .subscribe(data => {
                      this.form.get('name').patchValue(data.results.name);
                    });
                }
              );
            } else {
              this.partsService.addPart(this.form.getRawValue()).subscribe(
                res => {
                  if (res && res.responseCode === 200) {
                    this.toastr.success(PartsUIMessages.ADD_PART_SUCCESS);
                    this.router.navigate([`../${res.results.id}`], {
                      relativeTo: this.route
                    });
                  } else if (
                    res &&
                    res.responseCode === 400 &&
                    res.errorCode === 160
                  ) {
                    this.toastr.error(PartsUIMessages.ADD_PART_DUPLICATE);
                  } else {
                    this.toastr.error(PartsUIMessages.ADD_PART_ERROR);
                  }
                },
                error => {
                  this.toastr.error(error.errorMessage);
                },
                () => {
                  this.progressRef.complete();
                }
              );
            }
          }
        });
    }
  }

  onCancel(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  private createForm() {
    this.form = this.formBuilder.group({
      id: [this.partDetail ? this.partDetail.id : null],
      number: [
        this.partDetail ? this.partDetail.number : null,
        Validators.compose([Validators.required, Validators.maxLength(150)])
      ],
      name: [this.partDetail ? this.partDetail.name : null],
      description: [
        this.partDetail ? this.partDetail.description : null,
        Validators.compose([Validators.required, Validators.maxLength(150)])
      ],
      partTypeId: [this.partDetail ? this.partDetail.partTypeId : null],
      partClassificationId: [
        this.partDetail ? this.partDetail.partClassificationId : null
      ],
      legacyId: [this.partDetail ? this.partDetail.legacyId : null],
      makeId: [
        this.partDetail ? this.partDetail.makeId : null,
        Validators.required
      ],
      productLineId: [
        this.partDetail ? this.partDetail.productLineId : null,
        Validators.required
      ],
      subProductLineId: [
        this.partDetail ? this.partDetail.subProductLineId : null
      ],
      isActionProduct: [
        this.partDetail ? this.partDetail.isActionProduct : null,
        Validators.required
      ],
      equipmentTypeId: [
        this.partDetail ? this.partDetail.equipmentTypeId : null
      ],
      materialCostMinimum: [
        this.partDetail ? this.partDetail.materialCostMinimum : null,
        Validators.compose([Validators.required, Validators.max(99999999.99)])
      ],
      materialCostMaximum: [
        this.partDetail ? this.partDetail.materialCostMaximum : null,
        Validators.compose([Validators.required, Validators.max(99999999.99)])
      ],
      laborHoursMinimum: [
        this.partDetail ? this.partDetail.laborHoursMinimum : null,
        Validators.compose([Validators.required, Validators.max(99999999.99)])
      ],
      laborHoursMaximum: [
        this.partDetail ? this.partDetail.laborHoursMaximum : null,
        Validators.compose([Validators.required, Validators.max(99999999.99)])
      ],
      partCategoryId: [this.partDetail ? this.partDetail.partCategoryId : null],
      isActive: [
        this.partDetail ? this.partDetail.isActive : true,
        Validators.required
      ],
      unitOfMeasure: [
        this.partDetail ? this.partDetail.unitOfMeasure : null,
        Validators.maxLength(30)
      ],
      build: [
        this.partDetail ? this.partDetail.build : null,
        Validators.maxLength(30)
      ],
      isTruckStock: [
        this.partDetail ? this.partDetail.isTruckStock : null,
        Validators.required
      ]
    });
  }

  public getPartTypes(): void {
    this.partsService.getPartTypes().subscribe(data => {
      this.partTypes = data;
      this.partTypesFiltered = data;
    });
  }

  public onPartTypeFilterChange(value: any) {
    this.partTypesFiltered = this.partTypes.filter(
      partType =>
        partType.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  public onPartClassificationFilterChange(value: any) {
    this.partClassificationsFiltered = this.partClassifications.filter(
      partClassification =>
        partClassification.name.toLowerCase().indexOf(value.toLowerCase()) !==
        -1
    );
  }

  public onProductLineFilterChange(value: any) {
    this.productLinesFiltered = this.productLines.filter(
      productLine =>
        productLine.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  public onMakeFilterChange(value: any) {
    this.makesFiltered = this.makes.filter(
      make => make.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  public onPartCategoryFilterChange(value: any) {
    this.partCategoriesFiltered = this.partCategories.filter(
      category =>
        category.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  public onEquipmentTypeFilterChange(value: any) {
    this.equipmentTypesFiltered = this.equipmentTypes.filter(
      type => type.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  // if the part is associated to an inactive item, the function adds it to the list of active ones
  public showInactive(controlName: string, items: Array<any>) {
    const controlId = controlName + 'Id';
    const isFound = items.some(item => item.id === this.partDetail[controlId]);
    if (!isFound && this.partDetail[controlName]) {
      const item = {
        id: this.partDetail[controlId],
        name: this.partDetail[controlName]
      };
      items.push(item);
      items.sort((a: any, b: any) => SortUtils.compareByProperty(a, b, 'name'));
      this.form.get(controlId).patchValue(item.id);
    }
  }
}
